'use client';

import React from 'react';
import styled from 'styled-components';

/**
 * Interface for individual step configuration
 */
export interface Step {
  id: string;
  label: string;
  completed?: boolean;
  active?: boolean;
}

/**
 * Props for the ProgressStepper component
 */
interface ProgressStepperProps {
  /** Array of steps to display in the stepper */
  steps?: Step[];
  /** Current active step index (0-based) */
  currentStep?: number;
  /** Gutter spacing between steps */
  gutter?: 'sm' | 'md' | 'lg';
  /** Callback function when a step is clicked */
  onStepClick?: (stepIndex: number) => void;
}

const StepperContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 145px;

  @media (max-width: 800px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 20px 0;
  }
`;

const StepItem = styled.div<{ $isLast?: boolean; $clickable?: boolean; $completed?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 74px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  &:hover {
    opacity: ${props => (props.$clickable ? 0.8 : 1)};
  }

  ${props =>
    !props.$isLast &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 7px;
      top: 18px;
      width: 2px;
      height: 70px;
      background: ${props.$completed ? props.theme.colors.accent : props.theme.colors.gray[200]};
      z-index: 0;
      border-radius: ${props.theme.borderRadius.lg};
    }
  `}

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 0;
    flex: 1;
    position: relative;

    ${props =>
      !props.$isLast &&
      `
      &::after {
        content: '';
        position: absolute;
        left: calc(50% + 16px);
        top: 8px;
        width: calc(100% - 32px);
        height: 2px;
        background-color: ${props.$completed ? props.theme.colors.accent : props.theme.colors.gray[200]};
        z-index: 0;
        border-radius: ${props.theme.borderRadius.lg};
      }
    `}
  }
`;

const StepCircle = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: ${props => props.theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  margin-right: ${props => props.theme.spacing.sm};

  background: ${props => {
    if (props.$active || props.$completed) return 'linear-gradient(270deg, #D33324 0%, #FB6F2A 100%)';
    return 'transparent';
  }};

  border: ${props => {
    if (props.$active || props.$completed) return 'none';
    return `2px solid ${props.theme.colors.gray[200]}`;
  }};

  @media (max-width: 800px) {
    width: 24px;
    height: 24px;
    margin-right: 0;
    margin-bottom: 8px;

    background: ${props => {
      if (props.$active || props.$completed) return props.theme.colors.accent;
      return props.theme.colors.gray[300];
    }};

    border: none;
  }
`;

const StepInner = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: ${props => {
    if (props.$active || props.$completed) return '12px';
    return '0px';
  }};
  height: ${props => {
    if (props.$active || props.$completed) return '12px';
    return '0px';
  }};
  border-radius: ${props => props.theme.borderRadius.full};
  display: ${props => {
    if (props.$active || props.$completed) return 'flex';
    return 'none';
  }};
  align-items: center;
  justify-content: center;
  background: ${props => {
    if (props.$completed) return 'transparent';
    if (props.$active) return props.theme.colors.white;
    return 'transparent';
  }};

  &::after {
    content: ${props => {
      if (props.$completed) return '"✓"';
      return '""';
    }};
    width: ${props => {
      if (props.$completed) return 'auto';
      if (props.$active) return '8px';
      return '0px';
    }};
    height: ${props => {
      if (props.$completed) return 'auto';
      if (props.$active) return '8px';
      return '0px';
    }};
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => {
      if (props.$active && !props.$completed) return 'linear-gradient(270deg, #D33324 0%, #FB6F2A 100%)';
      return 'transparent';
    }};
    color: ${props => {
      if (props.$completed) return props.theme.colors.white;
      return 'transparent';
    }};
    font-size: 8px;
    font-weight: bold;
    line-height: 1;
  }

  @media (max-width: 800px) {
    display: none;
  }
`;

const StepLabel = styled.span<{ $active?: boolean; $completed?: boolean }>`
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: 1.2;
  font-weight: ${props => props.theme.fontWeights.normal};
  color: ${props => {
    if (props.$active) return props.theme.colors.accent;
    if (props.$completed) return props.theme.colors.success;
    return props.theme.colors.black;
  }};

  @media (max-width: 800px) {
    font-size: ${props => props.theme.fontSizes.sm};
    font-weight: ${props =>
      props.$active ? props.theme.fontWeights.medium : props.theme.fontWeights.normal};
    max-width: 100px;
    line-height: 1.2;
  }
`;

/**
 * ProgressStepper component for displaying step-by-step progress
 * 
 * @param steps - Array of step objects with id, label, and optional completed/active states
 * @param currentStep - Current active step index (0-based)
 * @param onStepClick - Callback function when a step is clicked
 * @returns JSX element representing the progress stepper
 */
export const ProgressStepper: React.FC<ProgressStepperProps> = ({
  steps,
  currentStep = 0,
  onStepClick,
}) => {
  const handleStepClick = (index: number) => {
    // Only allow clicking on completed steps or current step
    if (onStepClick && (index < currentStep || index === currentStep)) {
      onStepClick(index);
    }
  };

  return (
    <StepperContainer>
      {steps?.map((step, index) => {
        const isClickable = onStepClick && (index < currentStep || index === currentStep);
        const isCompleted = index < currentStep || step.completed;
        return (
          <StepItem
            key={step.id}
            $isLast={index === steps?.length - 1}
            $clickable={isClickable}
            $completed={isCompleted}
            onClick={() => handleStepClick(index)}
          >
            <StepCircle
              $active={index === currentStep || step.active}
              $completed={index < currentStep || step.completed}
            >
              <StepInner
                $active={index === currentStep || step.active}
                $completed={index < currentStep || step.completed}
              />
            </StepCircle>
            <StepLabel
              $active={index === currentStep || step.active}
              $completed={index < currentStep || step.completed}
            >
              {step.label}
            </StepLabel>
          </StepItem>
        );
      })}
    </StepperContainer>
  );
};

